import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  connectAuthEmulator,
  getAuth,
  setPersistence,
} from "firebase/auth";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { connectStorageEmulator, getStorage } from "firebase/storage";
import {
  type AppCheck,
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";

const useEmulators = import.meta.env.VITE_USE_EMULATORS === "true";
const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || "demo-gull";

const config = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || (useEmulators ? "demo-api-key" : ""),
  authDomain:
    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ||
    (useEmulators ? `${projectId}.firebaseapp.com` : ""),
  projectId,
  storageBucket:
    import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ||
    (useEmulators ? `${projectId}.appspot.com` : ""),
  messagingSenderId:
    import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || (useEmulators ? "000000000000" : ""),
  appId: import.meta.env.VITE_FIREBASE_APP_ID || (useEmulators ? "1:000000000000:web:demo" : ""),
};

const firebaseApp = initializeApp(config);
export const auth = getAuth(firebaseApp);
export const db = getFirestore(firebaseApp);
export const storage = getStorage(firebaseApp);

let appCheckInstance: AppCheck | null = null;

if (useEmulators) {
  connectAuthEmulator(auth, "http://127.0.0.1:9099", { disableWarnings: true });
  connectFirestoreEmulator(db, "127.0.0.1", 8080);
  connectStorageEmulator(storage, "127.0.0.1", 9199);
} else if (import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
  appCheckInstance = initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaEnterpriseProvider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

export async function getAppCheckHeader(): Promise<Record<string, string>> {
  if (!appCheckInstance) {
    if (!useEmulators) {
      console.warn("[app-check] VITE_RECAPTCHA_SITE_KEY is not configured for customer.");
    }
    return {};
  }
  try {
    const result = await getToken(appCheckInstance);
    return { "X-Firebase-AppCheck": result.token };
  } catch (err) {
    console.error("[app-check] Failed to get customer App Check token.", err);
    return {};
  }
}

void setPersistence(auth, browserLocalPersistence);

export const ENDUSER_API = import.meta.env.VITE_ENDUSER_API_URL || "/api/enduser";
